import React, { useState, useEffect } from 'react';
import { Translate } from 'react-localize-redux';
import signupCss from '../signup/style.css';
import wizardCss from './style.css';
import Button from '../components/atoms/Button';
import ProgressBar from '../components/molecules/ProgressBar';
import WizardSidebar from './WizardSidebar';
import { TRANSLATE_PREFIX, PARTNER_PATH, AGENCY_PATH, WIZARD_SESSION_KEY } from './consts';

function QuestionHeader({ current, total }) {
    const percentage = Math.round((current / total) * 100);
    return (
        <>
            <div className={wizardCss.progressHeader}>
                <span className={wizardCss.progressLabel}>
                    <Translate
                        id={`${TRANSLATE_PREFIX}.PROGRESS_LABEL`}
                        data={{ current, total }}
                    />
                </span>
                <span className={wizardCss.progressPercent}>{percentage}%</span>
            </div>
            <ProgressBar
                progressSections={[{ percentage }]}
                barHeight="10px"
                barWidth="100%"
                fillColor="#3A92E5"
                containerColor="#E9EEF4"
            />
        </>
    );
}

function OptionButton({ translationKey, onClick }) {
    return (
        <Button
            type="secondary"
            className={wizardCss.wizardOptionButton}
            onClick={onClick}
        >
            <Translate id={translationKey} />
        </Button>
    );
}

export default function AccountTypeWizard() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});

    useEffect(() => {
        sessionStorage.removeItem(WIZARD_SESSION_KEY);
    }, []);

    const recordAndNavigate = (questionNum, optionKey, destination) => {
        const updatedAnswers = { ...answers, [`q${questionNum}`]: optionKey };
        sessionStorage.setItem(WIZARD_SESSION_KEY, JSON.stringify(updatedAnswers));
        window.location.href = destination === 'agency' ? AGENCY_PATH : PARTNER_PATH;
    };

    const recordAndAdvance = (questionNum, optionKey, nextStep) => {
        setAnswers(prev => ({ ...prev, [`q${questionNum}`]: optionKey }));
        setStep(nextStep);
    };

    const questions = [
        {
            titleKey: `${TRANSLATE_PREFIX}.Q1_TITLE`,
            subtitleKey: `${TRANSLATE_PREFIX}.Q1_SUBTITLE`,
            subtitleOptions: { renderInnerHtml: true },
            options: [
                { key: `${TRANSLATE_PREFIX}.Q1_OPT_PARTNER`, onClick: () => recordAndNavigate(1, 'partner', 'partner') },
                { key: `${TRANSLATE_PREFIX}.Q1_OPT_AGENCY`, onClick: () => recordAndNavigate(1, 'agency', 'agency') },
                { key: `${TRANSLATE_PREFIX}.Q1_OPT_UNSURE`, onClick: () => recordAndAdvance(1, 'unsure', 1) },
            ],
        },
        {
            titleKey: `${TRANSLATE_PREFIX}.Q2_TITLE`,
            subtitleKey: `${TRANSLATE_PREFIX}.Q2_SUBTITLE`,
            options: [
                { key: `${TRANSLATE_PREFIX}.Q2_OPT_YES`, onClick: () => recordAndAdvance(2, 'yes', 2) },
                { key: `${TRANSLATE_PREFIX}.Q2_OPT_NO`, onClick: () => recordAndNavigate(2, 'no', 'partner') },
            ],
        },
        {
            titleKey: `${TRANSLATE_PREFIX}.Q3_TITLE`,
            subtitleKey: `${TRANSLATE_PREFIX}.Q3_SUBTITLE`,
            options: [
                { key: `${TRANSLATE_PREFIX}.Q3_OPT_YES`, onClick: () => recordAndAdvance(3, 'yes', 3) },
                { key: `${TRANSLATE_PREFIX}.Q3_OPT_NO`, onClick: () => recordAndNavigate(3, 'no', 'partner') },
            ],
        },
        {
            titleKey: `${TRANSLATE_PREFIX}.Q4_TITLE`,
            options: [
                { key: `${TRANSLATE_PREFIX}.Q4_OPT_YES`, onClick: () => recordAndNavigate(4, 'yes', 'agency') },
                { key: `${TRANSLATE_PREFIX}.Q4_OPT_NO`, onClick: () => recordAndNavigate(4, 'no', 'partner') },
            ],
        },
    ];

    const current = questions[step];

    return (
        <div className={signupCss.signupContainer}>
            <WizardSidebar />
            <div className={`${signupCss.signupForm} ${wizardCss.wizardForm}`}>
                <div className={wizardCss.wizardFormCard}>
                    <QuestionHeader current={step + 1} total={questions.length} />
                    {step > 0 && (
                        <div className={wizardCss.backRow}>
                            <button className={wizardCss.backButton} onClick={() => setStep(step - 1)} type="button">
                                ← <Translate id={`${TRANSLATE_PREFIX}.BACK_BUTTON`} />
                            </button>
                        </div>
                    )}
                    <div className={wizardCss.questionTitle}>
                        <Translate id={current.titleKey} />
                    </div>
                    {current.subtitleKey && (
                        <div className={wizardCss.questionSubtitle}>
                            <Translate id={current.subtitleKey} options={current.subtitleOptions} />
                        </div>
                    )}
                    <div className={wizardCss.optionsList}>
                        {current.options.map(opt => (
                            <OptionButton key={opt.key} translationKey={opt.key} onClick={opt.onClick} />
                        ))}
                    </div>
                </div>
                <div className={wizardCss.wizardFormFooter}>
                    <Translate id={`${TRANSLATE_PREFIX}.FOOTER_ALREADY_KNOW`} />
                    {' '}
                    <a className={wizardCss.wizardFooterLink} href={PARTNER_PATH}>
                        <Translate id={`${TRANSLATE_PREFIX}.FOOTER_PARTNER_LINK`} />
                    </a>
                    {' or '}
                    <a className={wizardCss.wizardFooterLink} href={AGENCY_PATH}>
                        <Translate id={`${TRANSLATE_PREFIX}.FOOTER_AGENCY_LINK`} />
                    </a>
                </div>
            </div>
        </div>
    );
}
